import { Button } from "@/components/ui/button";
import { SrIcon } from "@/components/ui/SrIcon";
import { useCustomerActivities, type CustomerActivityRow } from "@/hooks/use-customer-activities";
import { activityTypeLabel } from "@/lib/customer-activity-calc";
import { formatDate } from "@/lib/format";
import { EXCERPT_DRAG_TYPE, excerptFromActivity, type ExcerptFields } from "@/lib/tilbudsarbeidsrom/idea-board";

function toExcerpt(a: CustomerActivityRow): ExcerptFields {
  return excerptFromActivity({
    id: a.id,
    type: a.type,
    typeLabel: activityTypeLabel(a.type),
    subject: a.subject,
    body: a.body,
    occurred_at: a.occurred_at,
    userName: a.user?.full_name ?? null,
  });
}

interface IdeaSourcePanelProps {
  customerId: string | null;
  readOnly: boolean;
  onAdd: (excerpt: ExcerptFields) => void;
}

/**
 * Møtereferater, notater og e-post fra kundekortet (VIB-2980). Dra en
 * aktivitet til tavlen, eller bruk «Legg på tavlen», for å lage et sitatkort.
 */
export function IdeaSourcePanel({ customerId, readOnly, onAdd }: IdeaSourcePanelProps) {
  const { data: activities = [], isLoading } = useCustomerActivities(customerId ?? undefined, { limit: 30 });
  const usable = activities.filter((a) => (a.subject ?? "").trim() || (a.body ?? "").trim());

  return (
    <aside className="flex flex-col gap-3 min-w-0" aria-labelledby="idea-sources-heading">
      <div>
        <h2 id="idea-sources-heading" className="font-display text-base">Fra kundekortet</h2>
        <p className="text-xs text-muted-foreground">Dra til tavlen for å lage et sitatkort.</p>
      </div>

      {!customerId && (
        <p className="text-sm text-muted-foreground">Tilbudet er ikke koblet til en kunde i kunderegisteret.</p>
      )}
      {customerId && isLoading && <SrIcon name="loader2" className="w-4 h-4 animate-spin text-muted-foreground" />}
      {customerId && !isLoading && usable.length === 0 && (
        <p className="text-sm text-muted-foreground">Ingen notater, referater eller e-post på kundekortet ennå.</p>
      )}

      <ul className="flex flex-col gap-2 overflow-y-auto max-h-[70vh] pr-1">
        {usable.map((a) => (
          <li
            key={a.id}
            draggable={!readOnly}
            onDragStart={(e) => {
              e.dataTransfer.setData(EXCERPT_DRAG_TYPE, JSON.stringify(toExcerpt(a)));
              e.dataTransfer.effectAllowed = "copy";
            }}
            className="border border-border bg-card rounded-md p-3 flex flex-col gap-1 cursor-grab active:cursor-grabbing"
          >
            <span className="text-[11px] uppercase tracking-wide font-medium text-muted-foreground">
              {activityTypeLabel(a.type)} · {formatDate(a.occurred_at)}
              {a.user?.full_name ? ` · ${a.user.full_name}` : ""}
            </span>
            {a.subject && <span className="text-sm font-medium">{a.subject}</span>}
            {a.body && <span className="text-sm text-muted-foreground line-clamp-3">{a.body}</span>}
            {!readOnly && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="self-start h-7 px-2 text-xs"
                onClick={() => onAdd(toExcerpt(a))}
                aria-label={`Legg «${a.subject || activityTypeLabel(a.type)}» på tavlen`}
              >
                <SrIcon name="plus" className="w-3.5 h-3.5" />
                Legg på tavlen
              </Button>
            )}
          </li>
        ))}
      </ul>
    </aside>
  );
}
